'use client'
import { useState, useEffect, useCallback } from 'react'
import { useLocale } from 'next-intl'
import { getLanguageByCode, setLanguageCookie } from '@/src/utils/language'
import { languageOptions } from '@/src/constants/header'

export interface UseHeaderLanguageReturn {
    selectedLanguage: { code: string; label: string; Flag: React.ComponentType<any> }
    onLanguageSelect: (value: { code: string; label: string; Flag: React.ComponentType<any> }) => void
}

/** Hook for header language state and selection logic */
export const useHeaderLanguage = (
    variant: 'flyin' | 'cleartrip',
    isMounted: boolean
): UseHeaderLanguageReturn => {
    const locale = useLocale()
    const [selectedLanguage, setSelectedLanguage] = useState(() => getLanguageByCode(locale))

    // Always derive selectedLanguage from URL locale - never override with localStorage/cookie on back navigation
    useEffect(() => {
        const langOption = getLanguageByCode(locale)
        setSelectedLanguage(langOption)
    }, [locale])

    // Sync cookie and localStorage with URL locale when it changes (e.g. browser back to /en/)
    // This ensures stored preference matches the actual page the user is viewing
    useEffect(() => {
        if (variant === 'cleartrip' && isMounted && locale) {
            setLanguageCookie(locale)
            localStorage.setItem('selectedLanguage', locale)
        }
    }, [locale, variant, isMounted])

    const onLanguageSelect = useCallback(
        (value: { code: string; label: string; Flag: React.ComponentType<any> }) => {
            setLanguageCookie(value.code)

            if (variant === 'cleartrip' && isMounted) {
                localStorage.setItem('selectedLanguage', value.code)
                const base = window.location.href
                const newPath = value.code === 'ar' ? base.replace('/en', '/ar') : base.replace('/ar', '/en')
                window.location.href = newPath
            } else {
                const currentPath = window.location.pathname
                const currentSearch = window.location.search
                const currentHash = window.location.hash

                let newPath = currentPath
                if (currentPath.startsWith('/en')) {
                    newPath = value.code === 'ar' ? currentPath.replace(/^\/en/, '/ar') : currentPath
                } else if (currentPath.startsWith('/ar')) {
                    newPath = value.code === 'en' ? currentPath.replace(/^\/ar/, '/en') : currentPath
                } else {
                    newPath = `/${value.code}${currentPath}`
                }

                window.location.href = newPath + currentSearch + currentHash
            }
        },
        [variant, isMounted]
    )

    return {
        selectedLanguage,
        onLanguageSelect
    }
}
